import { Star } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useInsertMasterclassRating } from '@/hooks/queries';
import { cn } from '@/lib/utils';
import { captureSentry } from '@/utils/sentryHelper';

import { StarRating } from './StarRating';
import config from '../config/masterclass-rating-config.json';
import { useMasterclassRatingAnimation } from '../hooks/useMasterclassRatingAnimation';
import { MasterclassRatingModalProps } from '../types';

export const MasterclassRatingModal = ({
  caseId,
  userId,
  masterclassId,
  certificationProgressId,
  onRatingSubmitted,
  isOpen,
}: MasterclassRatingModalProps) => {
  const [applicationRating, setApplicationRating] = useState<number | null>(null);
  const [masterclassRating, setMasterclassRating] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const insertRatingMutation = useInsertMasterclassRating();

  useMasterclassRatingAnimation(isSubmitted);

  const handleSubmit = async () => {
    if (!applicationRating || !masterclassRating) {
      setShouldShake(true);
      toast.error('Please rate both sections');
      setTimeout(() => setShouldShake(false), 500);
      return;
    }

    try {
      await insertRatingMutation.mutateAsync({
        userId,
        caseId,
        masterclassId,
        certificationProgressId,
        applicationRating,
        masterclassRating,
      });

      setIsSubmitted(true);

      setTimeout(() => {
        toast.success(config.defaults.thankYouMessage);
        handleClose();
      }, config.animations.confetti.duration);
    } catch (error: any) {
      // Handle duplicate rating gracefully (unique constraint violation)
      if (error?.code === '23505') {
        console.log('Rating already exists for this user and masterclass');
        toast.success('You have already rated this masterclass');
        handleClose();
        return;
      }

      console.error('Error submitting masterclass rating:', error);
      captureSentry({
        title: 'Failed to submit masterclass rating',
        error,
        detail: { caseId, userId, certificationProgressId, applicationRating, masterclassRating },
      });
      toast.error('Unable to submit rating. Please try again.');
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onRatingSubmitted();
    }, config.animations.modal.exit.duration);
  };

  const isSubmitDisabled =
    !applicationRating || !masterclassRating || insertRatingMutation.isPending;

  return (
    <Dialog open={isOpen}>
      <DialogContent
        hideDefaultClose={true}
        className={cn(
          'p-8 sm:max-w-md',
          isClosing
            ? 'animate-[fade-out_0.3s_ease-out,_scale-out_0.2s_ease-out]'
            : 'animate-[fade-in_0.4s_ease-out,_scale-in_0.3s_ease-out]'
        )}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="animate-[fade-in_0.5s_ease-out]">
          <DialogTitle className="mb-2 text-center font-heading text-2xl text-livelinx-purple200">
            {config.defaults.title}
          </DialogTitle>
          <p className="mb-4 text-center font-body text-sm text-livelinx-grey200">
            {config.defaults.subtitle}
          </p>
        </DialogHeader>

        <div className="space-y-6">
          <div
            className="animate-[fade-in_0.6s_ease-out]"
            style={{ animationDelay: '0.2s', opacity: 0, animationFillMode: 'forwards' }}
          >
            <StarRating
              value={applicationRating}
              onChange={setApplicationRating}
              label={config.defaults.applicationQuestion}
            />
          </div>

          <div
            className="animate-[fade-in_0.6s_ease-out]"
            style={{ animationDelay: '0.4s', opacity: 0, animationFillMode: 'forwards' }}
          >
            <StarRating
              value={masterclassRating}
              onChange={setMasterclassRating}
              label={config.defaults.masterclassQuestion}
            />
          </div>
        </div>

        <div
          className="animate-[fade-in_0.6s_ease-out]"
          style={{ animationDelay: '0.6s', opacity: 0, animationFillMode: 'forwards' }}
        >
          <Button
            onClick={() => void handleSubmit()}
            disabled={isSubmitDisabled}
            className={cn(
              'relative mt-6 w-full overflow-hidden transition-all duration-500',
              shouldShake && 'animate-shake',
              isSubmitDisabled
                ? 'scale-95 cursor-not-allowed bg-livelinx-grey200 opacity-50'
                : 'scale-100 animate-[pulse_2s_ease-in-out_infinite] bg-primary opacity-100 hover:scale-105 hover:bg-primary/90 hover:shadow-lg',
              insertRatingMutation.isPending && 'animate-[pulse_0.5s_ease-in-out_3]'
            )}
          >
            {insertRatingMutation.isPending ? (
              <span className="flex items-center justify-center gap-2">
                <Star className="h-4 w-4 animate-spin" />
                Submitting...
              </span>
            ) : (
              config.defaults.submitButtonText
            )}
          </Button>

          {isSubmitDisabled && !insertRatingMutation.isPending && (
            <p className="mt-2 animate-[fade-in_0.3s_ease-out] text-center text-xs text-livelinx-grey200">
              {config.defaults.pleaseRateBothText}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
